import { isInViewport } from './util';
import type { RoughAnnotation } from 'rough-notation/lib/model';
import Highlight from './Highlight';
import type { Cuepoint, THighlight, WidgetConfig } from 'src/types';
import type {
  LegacyHighlight,
  LegacyHighlightWithSelector,
} from '@libertify.frontend/adapters';

// Function to find elements by position array instead of CSS selectors
export function findElementByPosition(
  position: number[],
  rootElement: Element = document.body,
): Element | null {
  if (!position || position.length === 0) {
    return null;
  }

  let currentElement: Element = rootElement;

  for (let i = 0; i < position.length; i++) {
    const childIndex = position[i];
    const children = Array.from(currentElement.children);

    if (childIndex < 0 || childIndex >= children.length) {
      return null; // Invalid index
    }

    currentElement = children[childIndex];
  }

  return currentElement;
}

// Function to find elements by CSS selector (original approach)
export function findElementBySelector(
  selector: string,
  rootElement: Element = document.body,
): Element | null {
  return rootElement.querySelector(selector);
}

export interface ProcessingStrategy<
  T extends {
    highlights?: Highlight[];
    annotations?: { [key: string]: RoughAnnotation[] };
    cuepoints?: Cuepoint[];
  },
> {
  process(
    highlights: LegacyHighlight[],
    config: WidgetConfig,
    cuepoints: Cuepoint[],
    options?: {
      shownHighlights?: Set<string>;
      createAnnotations?: boolean;
      scrollHandler?: EventListener;
      isEditor?: boolean;
    },
  ): T;
}

export class NewProcessingStrategy
  implements
    ProcessingStrategy<{ highlights: Highlight[]; cuepoints?: Cuepoint[] }>
{
  private highlights: Highlight[] = [];
  private processedSelectors = new Set<string>();

  constructor(
    private elementFinder: (
      selector: string | number[],
      rootElement?: Element,
    ) => Element | null = (
      selector: string | number[],
      rootElement?: Element,
    ) => {
      if (typeof selector === 'string') {
        return findElementBySelector(selector, rootElement);
      } else {
        return findElementByPosition(selector, rootElement);
      }
    },
  ) {}

  public process(
    highlights: LegacyHighlightWithSelector[],
    config: WidgetConfig,
    cuepoints?: Cuepoint[],
    options?: {
      shownHighlights?: Set<string>;
      createAnnotations?: boolean;
      scrollHandler?: EventListener;
      isEditor?: boolean;
    },
  ) {
    // Create individual highlights for each selector
    const clonedHighlights: THighlight[] = [];
    for (const highlight of highlights) {
      if (highlight.selectors && Array.isArray(highlight.selectors)) {
        let i = 0;
        for (const selector of highlight.selectors) {
          clonedHighlights.push({
            ...highlight,
            selector,
            id: highlight.id + `-${i++}`,
            nInSeries: i,
            original_id: highlight.id,
          });
        }
      } else {
        clonedHighlights.push({
          ...highlight,
          original_id: highlight.id,
          nInSeries: 1,
        });
      }
    }
    this.highlights = clonedHighlights
      .map((highlight) => {
        let cuepoint: Partial<Cuepoint> | undefined = cuepoints?.find(
          (cuepoint) =>
            cuepoint.highlight_id === highlight.original_id ||
            // DLY
            // not sure if we need to support this use case
            // it seems to be cuepoint model conversion source_id to sourceId
            // but fixing with any to avoid type errors
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (cuepoint as any).sourceId === highlight.original_id,
        );
        if (!cuepoint) {
          cuepoint = {
            start: highlight.time ?? 0,
          };
        }
        // If the cuepoint is not found, we don't create the highlight
        // if (!cuepoint) return null;

        // If we already processed this selector, drop the highlight
        // to not render multiple times the same rough notation (not looking good)
        if (this.processedSelectors.has(highlight?.selector?.start)) {
          return null;
        }
        try {
          return new Highlight(highlight, config, cuepoint, options);
        } catch (e) {
          console.warn(e);
          return null;
        }
      })
      .filter((highlight) => highlight !== null);
    this.setListeners(options);
    this.highlights.sort((a, b) => a.time - b.time);
    return { highlights: this.highlights };
  }

  private setListeners({
    scrollHandler,
  }: { scrollHandler?: EventListener } = {}) {
    const { highlights } = this;
    const cuepointReachedHandler = ({ highlightElementId }) => {
      const highlight = highlights.find(
        (highlight) => highlight.id === highlightElementId,
      );
      if (!highlight) {
        // The error of missing highlights is handled in process()
        return;
      }
      const { element } = highlight;
      if (!element) {
        throw new Error('Element not found');
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
      // Only scroll ones to the first element
      // in a highlight in a series of highlights
      // (i.e., ones that include multiple rects)
      // Also don't jerk the page if we already see the element and don't need scrolling
      const highlightIsInViewport = isInViewport(element);
      const isInsideWindow = isElementInsideWindow(element);
      if (
        highlight.nInSeries === 1 &&
        isInsideWindow &&
        !highlightIsInViewport
      ) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
      // auto dock on left corner
      // disabled for pixmania
      // const container = document.querySelector(
      //   '.' + HOST_CLASSNAME,
      // ) as HTMLElement;
      // const draggable = container.shadowRoot?.querySelector(
      //   '.react-draggable',
      // ) as HTMLElement;
      // if (!draggable) throw new Error('Could not find the draggable element');
      // container.style.bottom = '10px';
      // container.style.left = '10px';
      // container.style.transition = 'all 0.5s ease-in-out';
      // draggable.style.transition = 'transform 0.5s ease-in-out';
      // setTimeout(() => {
      //   draggable.style.transition = 'none';
      // }, 500);

      let scrollTimeout;
      // We need to wait until the scroll is finished before starting the animation
      function showAfterScroll() {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () {
          if (!highlight) return;
          window.removeEventListener('scroll', showAfterScroll);
          highlight.showAnnotation();
          if (scrollHandler) window.addEventListener('scroll', scrollHandler);
        }, 0);
      }
      if (!document.hidden && document.visibilityState === 'visible') {
        if (highlightIsInViewport) {
          highlight.showAnnotation();
          window.removeEventListener('scroll', showAfterScroll);
        } else {
          addEventListener('scroll', showAfterScroll);
        }
      } else {
        highlight.showAnnotation();
      }
    };
    // @ts-expect-error Legacy code
    window.cuepointReachedHandler = cuepointReachedHandler;
    document.addEventListener(
      'cuepointReached',
      cuepointReachedHandler as unknown as EventListener,
      false,
    );
  }
}
// Memoized computed styles
let cachedDocumentStyle;
let cachedBodyStyle;

function isElementInsideWindow(element) {
  // Get element's bounding rectangle
  const rect = element.getBoundingClientRect();

  // Get window dimensions
  const windowWidth = window.innerWidth;

  // Check if element is inside window width
  const isInsideWidth = rect.left >= 0 && Math.floor(rect.right) <= windowWidth;

  // Get memoized computed styles
  if (!cachedDocumentStyle) {
    cachedDocumentStyle = getComputedStyle(document.documentElement);
  }
  if (!cachedBodyStyle) {
    cachedBodyStyle = getComputedStyle(document.body);
  }

  const hasHiddenOverflow =
    cachedDocumentStyle.overflowX === 'hidden' ||
    cachedBodyStyle.overflowX === 'hidden' ||
    cachedDocumentStyle.overflow === 'hidden' ||
    cachedBodyStyle.overflow === 'hidden';

  // Return true if element is inside AND overflow is hidden
  return isInsideWidth && hasHiddenOverflow;
}
